import { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  DragOverlay,
  closestCenter,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Plus, Trash2, Edit2, Check, X, GripVertical } from "lucide-react";
import { useColumns, useColumnNames, useColumnsActions, useAllTasks } from "../stores";
import { AddColumnModal } from "./columns/AddColumnModal";
import { CategoryMultiSelect } from "../taskCategories/components/CategoryMultiSelect";
import type { ColumnDefinition } from "../types";

export function ColumnsOverview() {
  const { t } = useTranslation();
  const columnDefs = useColumns();
  const columnNames = useColumnNames();
  const tasks = useAllTasks();
  const { addColumn, updateColumn, deleteColumn, reorderColumns } = useColumnsActions();

  const [showAddModal, setShowAddModal] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<string | null>(null);

  // Zähle Tasks pro Column
  const taskCountByColumn = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const col of columnNames) {
      counts[col] = tasks.filter(t => t.column === col).length;
    }
    return counts;
  }, [columnNames, tasks]);

  const handleDragStart = (event: DragStartEvent) => {
    const columnName = event.active.id as string;
    setDraggedColumn(columnName);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setDraggedColumn(null);

    if (!over || active.id === over.id) return;

    const oldIndex = columnDefs.findIndex(c => c.name === (active.id as string));
    const newIndex = columnDefs.findIndex(c => c.name === (over.id as string));

    if (oldIndex !== -1 && newIndex !== -1) {
      const newColumns = arrayMove([...columnDefs], oldIndex, newIndex);
      reorderColumns(newColumns);
    }
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-gray-900">{t('columns.title')}</h1>
            <p className="text-xs text-gray-500">
              {t('columns.count', { count: columnNames.length, taskCount: tasks.length })}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {t('columns.dragHint')}
            </p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 border border-gray-200 rounded px-2 py-1 transition-colors"
          >
            <Plus size={14} />
            {t('columns.addButton')}
          </button>
        </div>

        <SortableContext items={columnNames} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {columnDefs.map((colDef) => (
              <SortableColumnCard
                key={colDef.name}
                column={colDef.name}
                columnDef={colDef}
                taskCount={taskCountByColumn[colDef.name] ?? 0}
                onUpdate={(newName) => updateColumn(colDef.name, newName)}
                onDelete={() => deleteColumn(colDef.name)}
                onUpdateDefaultCategories={(categoryIds) => updateColumn(colDef.name, undefined, categoryIds)}
              />
            ))}
          </div>
        </SortableContext>

        {columnNames.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-xs">{t('columns.noColumns')}</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-4 text-xs text-gray-600 hover:text-gray-700 hover:underline"
            >
              {t('columns.addFirst')}
            </button>
          </div>
        )}

        <AddColumnModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAdd={addColumn}
          existingColumns={columnNames}
        />
      </div>

      <DragOverlay>
        {draggedColumn && (
          <div className="bg-white rounded border border-gray-400 px-3 py-1.5 opacity-90">
            <div className="flex items-center gap-2">
              <GripVertical size={14} className="text-gray-400" />
              <span className="text-xs font-medium text-gray-900">{draggedColumn}</span>
            </div>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

interface SortableColumnCardProps {
  column: string;
  columnDef: ColumnDefinition;
  taskCount: number;
  onUpdate: (newName: string) => void;
  onDelete: () => void;
  onUpdateDefaultCategories: (categoryIds: string[]) => void;
}

function SortableColumnCard({
  column,
  columnDef,
  taskCount,
  onUpdate,
  onDelete,
  onUpdateDefaultCategories,
}: SortableColumnCardProps) {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(column);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== column) {
      onUpdate(trimmed);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditName(column);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleDelete = () => {
    onDelete();
    setShowDeleteConfirm(false);
  };

  const totalTasks = taskCount;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded border transition-all ${
        isDragging
          ? "opacity-50 border-gray-400"
          : "border-gray-200 hover:border-gray-300"
      }`}
    >
      <div className="px-3 py-1.5 flex items-center gap-2">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 touch-none"
          title={t('common.dragToReorder')}
        >
          <GripVertical size={14} />
        </button>

        {/* Column Name */}
        {isEditing ? (
          <div className="flex-1 flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSave}
              className="flex-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-gray-900 bg-white"
            />
            <button
              onClick={handleSave}
              className="p-1 text-green-600 hover:bg-green-50 rounded"
              title={t('common.save')}
            >
              <Check size={14} />
            </button>
            <button
              onClick={handleCancel}
              className="p-1 text-gray-500 hover:bg-gray-100 rounded"
              title={t('common.cancel')}
            >
              <X size={14} />
            </button>
          </div>
        ) : (
          <>
            <span className="flex-1 text-xs font-medium text-gray-900">{column}</span>

            {/* Task Count */}
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span>{t('columns.taskCount', { count: taskCount })}</span>
            </div>

            {/* Actions */}
            <button
              onClick={() => setIsEditing(true)}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
              title={t('common.rename')}
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
              title={t('common.delete')}
            >
              <Trash2 size={14} />
            </button>
          </>
        )}
      </div>

      {/* Default Categories */}
      <div className="px-3 py-1.5 border-t border-gray-100">
        <div className="text-xs font-medium text-gray-500 mb-1">
          {t('columns.defaultCategories')}
        </div>
        <CategoryMultiSelect
          selectedCategoryIds={columnDef.defaultCategoryIds}
          onChange={onUpdateDefaultCategories}
          allowInlineCreate={true}
          compact={true}
        />
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="px-3 py-2 bg-red-50 border-t border-red-200">
          <p className="text-xs text-red-800 mb-2">
            {t('columns.deleteConfirm', { name: column })}
            {totalTasks > 0 && (
              <span className="block mt-1 font-medium">
                {t('columns.deleteWarning', { count: totalTasks })}
              </span>
            )}
          </p>
          <div className="flex gap-1.5">
            <button
              onClick={handleDelete}
              className="text-xs text-red-600 border border-red-200 rounded px-2 py-0.5 hover:bg-red-100"
            >
              {t('columns.yesDelete')}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="text-xs text-gray-600 hover:bg-gray-200 rounded px-2 py-0.5"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
